import { Box, Grid, Paper, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getUser } from "auth/helpers/authApi";
import { useRoomTemplateLoginPageContext } from "context/RoomTemplateContext";
import { useSocketContext } from "context/SocketContext";
import { EmbeddedPuzzleWindow } from "embedded-puzzle/EmbeddedPuzzleWindow";
import { NoteWindow } from "fileBrowser/components/notes/NoteWindow";
import { GameTutorialElements } from "gameTutorial/types";
import "intro.js/introjs.css";
import * as React from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useIntl } from "react-intl";
import { TaskbarItemContainer } from "../components/Taskbar";
import { useGameContext, useGameRoom } from "../context/GameContext";
import { WindowTilesProvider, useWindowTilesContext } from "../desktopWindow/context/WindowTilesContext";
import { EmailWindow } from "../email/components/EmailWindow";
import { FileBrowserWindow } from "../fileBrowser/components/FileBrowserWindow";
import { FileViewerWindow } from "../fileBrowser/components/FileViewerWindow";
import GameCallWindow from "../twilio/components/GameCallWindow";
import { GameDesktopFiles } from "./GameDesktopFiles";
import GameSteps from "./GameSteps";
import { useAuthUser } from "context/GameAuthContext";
import { TestModeButton } from "components/TestModeButton";
import TaskbarMacOS from "components/TaskbarMacOS";
import { ChatWindow } from "chat/components/ChatWindow";
import { GameTopbar } from "./GameTopbar";

const GameScreenContainer = styled("div")(({ theme }) => ({
	height: "100%",
	width: "100%",
	background: theme.organization.backgroundURI
		? `no-repeat center center fixed url(${theme.organization.backgroundURI})`
		: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
	backgroundSize: "cover",
	overflow: "hidden",
}));

const GameScreen: React.FC = () => {
	const { room, leaveRoom } = useGameContext();
	const user = useAuthUser();

	return (
		<>
			<GameScreenContainer className={GameTutorialElements.INTRO_MODAL}>
				{room && (
					<GameSteps
						organizationName={room?.organization.name}
						chatBotConfigBotName={room?.chatBotConfig.botName}
						playingTimeInMillis={room?.playingTimeInMillis}
					/>
				)}
				<WindowTilesProvider>
					{isMobile && <MobileTopBar />}
					{isDesktop && <GameTopbar />}
					<GameDesktopFiles />
					<GameWindow />
					<GameTaskbar />
					{isDesktop && <GameCallWindow />}
				</WindowTilesProvider>
				{isDesktop && user.isTestMode && (
					<Box position="fixed" top="48px" right="24px">
						<TestModeButton onClick={leaveRoom} />
					</Box>
				)}
			</GameScreenContainer>
		</>
	);
};

const GameWindow = () => {
	const { windowTiles } = useWindowTilesContext();
	return (
		<>
			{windowTiles.map((tile) => {
				if (tile.componentName === "Chat") {
					return <ChatWindow key={tile.id} tileId={tile.id} />;
				} else if (tile.componentName === "FileBrowser") {
					return <FileBrowserWindow key={tile.id} tileId={tile.id} />;
				} else if (tile.componentName === "FileViewer") {
					return <FileViewerWindow key={tile.id} tileId={tile.id} />;
				} else if (tile.componentName === "Email") {
					return <EmailWindow key={tile.id} tileId={tile.id} />;
				} else if (tile.componentName === "Notes") {
					return <NoteWindow key={tile.id} tileId={tile.id} />;
				} else if (tile.componentName === "EmbeddedPuzzle") {
					return <EmbeddedPuzzleWindow key={tile.id} tileId={tile.id} />;
				}
				return null;
			})}
		</>
	);
};

const GameTaskbar = () => {
	return <TaskbarMacOS />;
};

export const MobileTopBar = () => {
	const intl = useIntl();
	const { value: roomTemplate } = useRoomTemplateLoginPageContext();
	const { getHelp = false } = roomTemplate?.featureConfig || {};
	const classes = useTopBarStyles({ getHelp: getHelp });
	const { room, leaveRoom } = useGameContext();
	const { socket } = useSocketContext();
	const { roomId } = useGameRoom();
	const [isHelpAlreadyAsked, setHelpAlreadyAsked] = React.useState(false);

	const onHelpButtonClick = () => {
		if (!isHelpAlreadyAsked) {
			socket?.emit("helpAsked", { user: getUser(), roomId: roomId });
		}
	};

	React.useEffect(() => {
		if (room) {
			const help = (room as any)?.help.find((i) => i.isLive);
			if (help) {
				setHelpAlreadyAsked(true);
			} else {
				setHelpAlreadyAsked(false);
			}
		}
	}, [room]);

	return (
		<Paper className={classes.mobileTopBar}>
			{getHelp && (
				<Grid item className={`${GameTutorialElements.HELP_BUTTON} ${classes.MobileHelpIconContainer}`}>
					<TaskbarItemContainer
						testId="HelpBtn"
						title={intl.formatMessage({
							id: isHelpAlreadyAsked ? "TASKBAR.awaiting_help.button" : "TASKBAR.get_help.button",
						})}
						onClick={onHelpButtonClick}
						className={GameTutorialElements.HELP_BUTTON}
						mobile={true}
					/>
				</Grid>
			)}
			<Grid item className={`${GameTutorialElements.LOGOUT_BUTTON} ${classes.MobileExitIconContainer}`}>
				<TaskbarItemContainer
					testId="LeaveBtn"
					title={intl.formatMessage({ id: "TASKBAR.leave.button" })}
					onClick={leaveRoom}
					mobile={true}
				/>
			</Grid>
		</Paper>
	);
};

export default GameScreen;

const useTopBarStyles = makeStyles((theme) => ({
	mobileTopBar: {
		[theme.breakpoints.down("mobile")]: {
			position: "fixed",
			top: 0,
			left: 0,
			height: "41px",
			width: "100%",
			display: "flex",
			justifyContent: (props: any) => (props.getHelp ? "space-between" : "flex-end"),
			borderRadius: 0,
			zIndex: 1,
		},
	},
	MobileExitIconContainer: {
		width: "30px",
		height: "30px",
		alignItems: "flex-start",
		marginRight: 10,
	},
	MobileHelpIconContainer: {
		marginLeft: 10,
	},
}));
